import React, { useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useProducts } from './useProducts';
import { HomeCatalogShelves, HomeFeaturedShelf, HomeNewArrivalsShelf } from './HomeDynamicCatalog';
import HomeNextExhibitionBanner from './HomeNextExhibitionBanner';
import { useExhibitions } from './useExhibitions';
import { useSiteSettings } from './SiteSettingsContext';
import { getMergedStoreCopy } from './storeCopy';
import PageSeoHelmet from './PageSeoHelmet';
import StorefrontHero from './components/StorefrontHero';
import HomeInstagramSection from './components/home/HomeInstagramSection';
import RecentlyViewedStrip from './recentlyViewed/RecentlyViewedStrip';
import { usePullToRefresh } from './usePullToRefresh';
import HomeAssistantCard from './components/home/HomeAssistantCard';
import HomeTrustStrip from './components/home/HomeTrustStrip';
import HomeMerchandisingTiles from './components/home/HomeMerchandisingTiles';
import HomeProductShelves from './components/home/HomeProductShelves';
import HomeTestimonials from './components/home/HomeTestimonials';
import HomeShopEmptyStrip from './components/home/HomeShopEmptyStrip';
import { customerCatalogErrorMessage } from './storefront/customerFacingErrors';

const Home = () => {
  const { allProducts, refetch: refetchCatalog, loading: catalogLoading, error: catalogError } = useProducts();
  const exhibitions = useExhibitions();
  const { settings: site, refresh: refreshSettings } = useSiteSettings();
  const store = getMergedStoreCopy(site);

  const handleRefresh = useCallback(async () => {
    await Promise.all([
      refetchCatalog({ silent: true, force: true }),
      refreshSettings(),
      exhibitions.refetch(),
    ]);
  }, [exhibitions.refetch, refetchCatalog, refreshSettings]);

  const { pulling, ready, refreshing, pullDistance, threshold } = usePullToRefresh(handleRefresh, {
    threshold: 110,
  });

  const homeCopy = site.page_copy?.home || {};
  const showExhibition = exhibitions.status === 'ready' && exhibitions.upcoming.length > 0;
  const showExhibitionsError = exhibitions.status === 'error';
  const showCatalogError =
    Boolean(catalogError) && !catalogLoading && allProducts.length === 0;
  const showShopEmpty =
    !catalogLoading && !catalogError && allProducts.length === 0;
  const catalogErrorText = customerCatalogErrorMessage(catalogError, store.shop_catalog_error_text);
  const whatsappHref = site.whatsapp_href || '';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  /* Wine band fills header clearance — avoids cream gap when announcement bar is off */
  useEffect(() => {
    const main = document.getElementById('main-content');
    if (!main) return undefined;
    main.classList.add('main-content--home-luxury');
    return () => main.classList.remove('main-content--home-luxury');
  }, []);

  return (
    <div className="Home-main Home-main--luxury">
      <PageSeoHelmet page="home" />

      {(pulling || refreshing) ? (
        <div
          className={`home-pull-refresh${ready ? ' home-pull-refresh--ready' : ''}${refreshing ? ' home-pull-refresh--active' : ''}`}
          aria-live="polite"
          aria-hidden={!pulling && !refreshing}
          style={{ '--pull-progress': `${Math.min(100, (pullDistance / threshold) * 100)}%` }}
        >
          <span className="home-pull-refresh__label">
            {refreshing
              ? store.pull_refresh_refreshing
              : ready
                ? store.pull_refresh_release
                : store.pull_refresh_pull}
          </span>
        </div>
      ) : null}

      <div
        className={`home-luxury-band home-luxury-band--editorial${
          showExhibition ? ' home-luxury-band--has-exhibition' : ' home-luxury-band--no-exhibition'
        }`}
      >
        {showExhibition ? (
          <div className="home-top-strips home-top-strips--luxury">
            <HomeNextExhibitionBanner upcoming={exhibitions.upcoming} homeCopy={homeCopy} tone="editorial" />
          </div>
        ) : null}

        <StorefrontHero />
      </div>

      {showExhibitionsError ? (
        <div className="container store-page__container">
          <div className="alert alert-light border small d-flex flex-column flex-md-row align-items-md-center gap-2 mb-0" role="status">
            <span className="flex-grow-1">{store.home_exhibitions_error_text}</span>
            <Link to="/exhibitions" className="btn btn-outline-primary btn-sm rounded-pill flex-shrink-0">
              {store.home_exhibitions_error_link}
            </Link>
          </div>
        </div>
      ) : null}

      {showCatalogError ? (
        <div className="container store-page__container">
          <div className="alert alert-warning home-catalog-alert d-flex flex-column flex-md-row align-items-md-center gap-3 mb-0" role="alert">
            <div className="flex-grow-1">
              <p className="fw-semibold mb-1">{store.home_catalog_error_title}</p>
              <p className="mb-0 small">{catalogErrorText || store.home_catalog_error_text}</p>
            </div>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm rounded-pill flex-shrink-0"
              onClick={() => refetchCatalog()}
            >
              {store.home_catalog_retry}
            </button>
          </div>
        </div>
      ) : null}

      {showShopEmpty ? <HomeShopEmptyStrip store={store} whatsappHref={whatsappHref} /> : null}

      <HomeTrustStrip />

      <HomeMerchandisingTiles />

      <HomeFeaturedShelf />

      <HomeNewArrivalsShelf />

      <HomeProductShelves />

      <HomeCatalogShelves />

      <HomeAssistantCard site={site} />

      <HomeTestimonials />

      <RecentlyViewedStrip className="recently-viewed home-recently-viewed" />

      <HomeInstagramSection site={site} homeCopy={homeCopy} allProducts={allProducts} />
    </div>
  );
};

export default Home;
